import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { getProperties } from '../api/properties';
import { getIntercomConfigs, IntercomConfig } from '../api/intercomConfig';
import { bridgeClaimApi } from '../api/signShopOrders';

interface PropertyRef { id: string | number; name?: string; address?: string; }

export const ClaimBridge: React.FC = () => {
  // Pre-fill code from ?code=XXXXX-XXXXX (set by the label's QR code).
  const initialCode = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return (p.get('code') || '').toUpperCase();
  }, []);

  const [code, setCode] = useState(initialCode);
  const [displayName, setDisplayName] = useState('');
  const [propertyId, setPropertyId] = useState<string>('');
  const [intercomId, setIntercomId] = useState<string>('');
  const [properties, setProperties] = useState<PropertyRef[]>([]);
  const [intercoms, setIntercoms] = useState<IntercomConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getProperties().then((rows: any[]) => {
      setProperties((rows || []).map(r => ({ id: r.id, name: r.name, address: r.address })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!propertyId) { setIntercoms([]); setIntercomId(''); return; }
    getIntercomConfigs(propertyId).then(setIntercoms).catch(() => setIntercoms([]));
    setIntercomId('');
  }, [propertyId]);

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bridgeClaimApi.activateByCode({
        activation_code: code.trim().toUpperCase(),
        propertyId: propertyId || undefined,
        intercomId: intercomId || undefined,
        displayName: displayName || undefined,
      });
      setResult(res);
    } catch (e: any) {
      setError(e?.message || 'Claim failed');
    } finally {
      setLoading(false);
    }
  };

  const validCode = code.trim().replace(/-/g, '').length >= 8;

  if (result) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="shadow-page-frame shadow-readable-form mt-12">
          <Card>
            <div className="text-center">
              <img src="/intrlock-logo-dark-horizontal.png" alt="Intrlock" className="h-10 w-auto mx-auto mb-4" />
              <div className="text-4xl mb-2">✓</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bridge activated</h2>
              <p className="text-gray-600 mb-1">
                <code className="bg-gray-100 px-2 py-1 rounded">{result.serial}</code>
              </p>
              <div className="flex justify-center gap-2 mb-4 mt-2">
                <Badge color="gray">{result.role}</Badge>
                {result.has_cellular && <Badge color="blue">cellular</Badge>}
              </div>
              {result.displayName && <p className="text-gray-700 mb-1">{result.displayName}</p>}
              <p className="text-gray-500 text-sm mb-6">
                Plug it in, wait ~60 seconds. It will appear in your Bridges list when connected.
              </p>
              <Button onClick={() => { window.location.href = '/bridges'; }}>View bridges</Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="shadow-page-frame shadow-readable-form">
        <div className="mb-6 flex items-center gap-4">
          <img src="/intrlock-logo-dark-horizontal.png" alt="Intrlock" className="h-10 w-auto" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Claim a bridge</h1>
            <p className="text-gray-500 mt-1">
              Enter the 10-character activation code from your Intrlock bridge label, or scan the QR.
            </p>
          </div>
        </div>
        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Activation code *</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="XXXXX-XXXXX"
                autoComplete="off"
                autoCapitalize="characters"
                className="w-full border border-gray-300 rounded px-3 py-2 text-lg font-mono tracking-widest text-center"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Assign to property</label>
                <select value={propertyId} onChange={e => setPropertyId(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">— select —</option>
                  {properties.map(p => (
                    <option key={String(p.id)} value={String(p.id)}>
                      {p.name || p.address || `Property ${p.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Intercom (optional)</label>
                <select
                  value={intercomId}
                  onChange={e => setIntercomId(e.target.value)}
                  disabled={!propertyId}
                  className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
                >
                  <option value="">— none —</option>
                  {intercoms.map(c => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {c.label || `Intercom ${c.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display name (optional)</label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="e.g. Front gate relay"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            {error && <div className="text-sm text-red-700">{error}</div>}
            <div className="flex justify-end">
              <Button onClick={submit} loading={loading} disabled={!validCode}>
                Claim bridge
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
